// Das Übungsufer, Aussehen (Fabi, 26.10.2026: „Es soll super werden, auch schön“): Anschlüsse für die Bilder, die der
// Grafik-Lead fürs Übungsufer zeichnet (Auftrag in docs/uebergabe/an-grafik-lead-uebungsufer.md). Steht ein Bild im
// Manifest, nimmt das Übungsufer es; fehlt es, bleibt der Platzhalter aus Code, und nichts bricht.
// Reine Darstellung; public/art/ gehört dem Grafik-Lead, hier wird nur gelesen.
import { combatRules } from '../../content/index.ts';
import { blit, drawStrip, image, pixelScale, sprite, sprites, stripFrame, type ArtSprite } from '../art.ts';

const FIELD = combatRules.field;

/** Das Bild eines Streifens zur Zeit t, mit sequence oder ms je Bild, in Schleife */
export function loopFrame(sp: ArtSprite, t: number): number {
  if (sp.sequence?.length) {
    const total = sp.sequence.reduce((a, [, ms]) => a + ms, 0);
    let ms = ((t * 1000) % total + total) % total;
    for (const [f, d] of sp.sequence) {
      ms -= d;
      if (ms < 0) return f;
    }
    return sp.sequence[sp.sequence.length - 1]![0];
  }
  return stripFrame(sp.kind, sp.id, t);
}

/**
 * Ein Bild, das auf einem Punkt im Feld steht (marker mit size und anchor, wie der Baum des Eichhörnchens; mit frames
 * ein Streifen, center ist dann der Punkt). x, y in Feldeinheiten. false, wenn es das Bild nicht gibt.
 */
export function drawMarker(ctx: CanvasRenderingContext2D, s: number, id: string, x: number, y: number, t = 0, opts: { alpha?: number; flip?: boolean; frame?: number } = {}): boolean {
  const sp = sprite('marker', id);
  const img = sp ? image(sp.file) : null;
  if (!sp || !img) return false;
  const k = pixelScale(s);
  if (sp.frames && sp.frames > 1) return drawStrip(ctx, 'marker', id, opts.frame ?? loopFrame(sp, t), x * s, y * s, k, opts);
  const [w, h] = sp.size ?? sp.frameSize ?? [img.naturalWidth, img.naturalHeight];
  const a = sp.anchor ?? sp.center ?? [Math.floor(w / 2), h - 1];
  ctx.save();
  if (opts.alpha !== undefined) ctx.globalAlpha *= opts.alpha;
  blit(ctx, img, 0, 0, w, h, x * s, y * s, a, k, opts.flip);
  ctx.restore();
  return true;
}

/** Ein Streifen (effect, status, marker) zur Zeit t in Schleife, mittig auf (x, y) in Feldeinheiten; false, wenn er fehlt */
export function drawLoop(ctx: CanvasRenderingContext2D, s: number, kind: ArtSprite['kind'], id: string, x: number, y: number, t: number, opts: { alpha?: number; flip?: boolean } = {}): boolean {
  const sp = sprite(kind, id);
  if (!sp) return false;
  return drawStrip(ctx, kind, id, loopFrame(sp, t), x * s, y * s, pixelScale(s), opts);
}

/** Von mehreren Größen eines Bildes (id-12, id-13 …, Feld radius wie bei bedroht-<rx>) die, die zum Wesen passt */
export function byRadius(kind: ArtSprite['kind'], prefix: string, radius: number): ArtSprite | undefined {
  const list = sprites().filter((sp) => sp.kind === kind && sp.id.startsWith(prefix) && sp.radius !== undefined);
  if (!list.length) return sprite(kind, prefix.replace(/-$/, ''));
  return list.reduce((a, b) => (Math.abs(b.radius! - radius) < Math.abs(a.radius! - radius) ? b : a));
}

// ---------- Boden, Licht, Laub ----------

function fieldImage(ctx: CanvasRenderingContext2D, s: number, img: HTMLImageElement): void {
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, 0, 0, FIELD.width * s, FIELD.height * s);
}

/**
 * Der Boden des Übungsufers: ein Bild über das ganze Feld (ui boden-uebungsufer, 640 × 400 Bildpixel bei 4 je
 * Feldeinheit). Im Starkregen blendet die nasse Fassung (boden-uebungsufer-nass) langsam darüber. false ohne Bild.
 */
export function drawPracticeGround(ctx: CanvasRenderingContext2D, s: number, rain: number): boolean {
  const sp = sprite('ui', 'boden-uebungsufer');
  const img = sp ? image(sp.file) : null;
  if (!img) return false;
  fieldImage(ctx, s, img);
  const wet = sprite('ui', 'boden-uebungsufer-nass');
  const wi = wet ? image(wet.file) : null;
  if (wi && rain > 1) {
    ctx.save();
    ctx.globalAlpha = Math.min(1, (rain - 1) * 1.5);
    fieldImage(ctx, s, wi);
    ctx.restore();
  }
  return true;
}

/**
 * Licht über dem Feld (ui licht-uebungsufer oder, Runde 8, licht-<biom>, so groß wie der Boden), nach allen Figuren,
 * gemischt nach blend. wet (0 bis 1): im Starkregen gilt alphaNass statt alpha; weaker dämpft es (näheres Unwetter).
 * Rückgabe: ob ein Bild da war
 */
export function drawPracticeLight(ctx: CanvasRenderingContext2D, s: number, id = 'licht-uebungsufer', wet = 0, weaker = 0): boolean {
  const sp = sprite('ui', id);
  const img = sp ? image(sp.file) : null;
  if (!sp || !img) return false;
  const alpha = sp.alpha ?? 1;
  const a = (alpha * (1 - wet) + (sp.alphaNass ?? alpha) * wet) * (1 - weaker);
  if (a <= 0.002) return true;
  ctx.save();
  ctx.globalCompositeOperation = (sp.blend ?? 'soft-light') as GlobalCompositeOperation;
  ctx.globalAlpha = a;
  fieldImage(ctx, s, img);
  ctx.restore();
  return true;
}

/** So viele Blätter treiben übers Feld [ZAHL?] */
const LEAVES = 9;

/** Fallendes Laub (effect laub-fallend, ein Blatt, das sich dreht): ein paar Blätter treiben langsam schräg übers Feld */
export function drawLeaves(ctx: CanvasRenderingContext2D, s: number, T: number): void {
  const sp = sprite('effect', 'laub-fallend');
  if (!sp || !image(sp.file)) return;
  const W = FIELD.width + 24;
  const H = FIELD.height + 16;
  for (let i = 0; i < LEAVES; i++) {
    // goldener Schnitt: gleichmäßig verstreut, jedes Blatt mit eigenem Tempo und eigenem Schaukeln
    const speed = 3 + ((i * 0.7548) % 1) * 3;
    const y = ((T * speed + ((i * 0.381966) % 1) * H) % H) - 8;
    const x = ((((i * 0.618034) % 1) * W + y * 0.45 + Math.sin(T * 0.9 + i * 1.7) * 3) % W) - 12;
    drawLoop(ctx, s, 'effect', 'laub-fallend', x, y, T + i * 0.37, { alpha: 0.85, flip: i % 2 === 1 });
  }
}

// ---------- Mauszeiger ----------

const cursorUrls = new Map<string, string>();

/** Mauszeiger aus einem Bild (ui zeiger-…, anchor ist die Spitze, scale die Vergrößerung); sonst der Ersatz */
export function cursorFor(id: string, fallback: string): string {
  const sp = sprite('ui', id);
  const img = sp ? image(sp.file) : null;
  if (!sp || !img) return fallback;
  const k = sp.scale ?? 1;
  let url = cursorUrls.get(id);
  if (!url) {
    const [w, h] = sp.size ?? sp.frameSize ?? [img.naturalWidth, img.naturalHeight];
    const c = document.createElement('canvas');
    c.width = w * k;
    c.height = h * k;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    g.drawImage(img, 0, 0, w, h, 0, 0, w * k, h * k);
    url = c.toDataURL();
    cursorUrls.set(id, url);
  }
  const [ax, ay] = sp.anchor ?? [0, 0];
  return `url(${url}) ${ax * k} ${ay * k}, ${fallback}`;
}

// ---------- Rahmen, Zettel, Knöpfe ----------

/** Neunteiler (ui mit slice): Ränder bleiben, die Mitte und die Kanten wachsen mit */
const NINE = ['rahmen-holz', 'zettel', 'zettel-lila', 'zettel-gold', 'knopf-holz', 'knopf-holz-darueber', 'knopf-holz-gedrueckt', 'knopf-gold', 'knopf-gold-darueber', 'knopf-gold-gedrueckt', 'leiste-karte', 'leiste-karte-gewaehlt'];

/**
 * Rahmen, Zettel und Knöpfe aus Bildern: als CSS-Variablen (--art-<id>, --art-<id>-slice, --art-<id>-width), die
 * style.css nimmt, sobald body die Klasse art-<id> trägt. Das Seil am Zettel (ui seil) ist ein waagrechter Streifen.
 */
export function applyFrameArt(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const url = (file: string) => `url("${new URL(`art/${file}`, document.baseURI).href}")`;
  for (const id of NINE) {
    const sp = sprite('ui', id);
    const on = !!sp?.slice && !!image(sp.file);
    document.body.classList.toggle(`art-${id}`, on);
    if (!sp?.slice || !on) continue;
    const k = sp.scale ?? 1;
    const [t, r, b, l] = sp.slice;
    root.style.setProperty(`--art-${id}`, url(sp.file));
    root.style.setProperty(`--art-${id}-slice`, `${t} ${r} ${b} ${l}`);
    root.style.setProperty(`--art-${id}-width`, `${t * k}px ${r * k}px ${b * k}px ${l * k}px`);
    if (id === 'zettel') root.style.setProperty('--art-zettel-top', `${t * k}px`);
  }
  // T38: die Reißzwecke, mit der ein Zettel angeheftet wird
  const pin = sprite('ui', 'zettel-zwecke');
  const pinOn = !!pin && !!image(pin.file);
  document.body.classList.toggle('art-zettel-zwecke', pinOn);
  if (pin && pinOn) {
    const [w, h] = pin.size ?? [image(pin.file)!.naturalWidth, image(pin.file)!.naturalHeight];
    const k = pin.scale ?? 1;
    root.style.setProperty('--art-zettel-zwecke', url(pin.file));
    root.style.setProperty('--art-zettel-zwecke-w', `${w * k}px`);
    root.style.setProperty('--art-zettel-zwecke-h', `${h * k}px`);
  }
  const rope = sprite('ui', 'seil');
  const ropeOn = !!rope && !!image(rope.file);
  document.body.classList.toggle('art-seil', ropeOn);
  if (rope && ropeOn) {
    const [, h] = rope.size ?? rope.frameSize ?? [0, image(rope.file)!.naturalHeight];
    root.style.setProperty('--art-seil', url(rope.file));
    root.style.setProperty('--art-seil-h', `${h * (rope.scale ?? 1)}px`);
  }
}

// ---------- Kniff-Zeichen in der Leiste ----------

/**
 * Das Zeichen eines Kniffs für den Knopf in der Leiste (ui kniff-<id in abilities.json>; Bild 0 wartet, Bild 1 ist bereit,
 * wenn es zwei gibt), als kleine Leinwand; null ohne Bild
 */
export function kniffIcon(kniff: string, ready: boolean): HTMLCanvasElement | null {
  const sp = sprite('ui', `kniff-${kniff}`);
  const img = sp ? image(sp.file) : null;
  if (!sp || !img) return null;
  const [w, h] = sp.frameSize ?? sp.size ?? [img.naturalWidth, img.naturalHeight];
  const n = Math.max(1, sp.frames ?? 1);
  const k = sp.scale ?? 1;
  const c = document.createElement('canvas');
  c.width = w * k;
  c.height = h * k;
  c.className = 'pr-kicon';
  const g = c.getContext('2d')!;
  g.imageSmoothingEnabled = false;
  const f = ready && n > 1 ? 1 : 0;
  g.drawImage(img, f * w, 0, w, h, 0, 0, w * k, h * k);
  return c;
}
